/*###########################################################################
 // Binder overview, editor, and CSS-3D viewer (Alpine components)
 //#########################################################################*/

import { showConfirm, showPrompt, showChecklist } from './_confirmDialog'
import { showSiteToast } from './_siteToast'

function readJson(id) {
  const el = document.getElementById(id)
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

async function postJson(url, body, csrfName, csrfToken) {
  const payload = new FormData()
  payload.append(csrfName, csrfToken)
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    payload.append(key, String(value))
  })

  const res = await fetch(url, {
    method: 'POST',
    body: payload,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`)
  }
  return data
}

async function getJson(url) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`)
  }
  return data
}

/** Append query params without breaking Craft action URLs that already have `?`. */
function withParams(base, params = {}) {
  const url = new URL(base, window.location.origin)
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    url.searchParams.set(key, String(value))
  })
  return url.toString()
}

function filterByQuery(items, query, fields) {
  const q = query.trim().toLowerCase()
  if (!q) return items
  return items.filter((item) => {
    const hay = fields.map((f) => String(item[f] ?? '')).join(' ').toLowerCase()
    return hay.includes(q)
  })
}

async function copyText(text) {
  if (!text) return false
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // fall through
    }
  }
  try {
    const input = document.createElement('textarea')
    input.value = text
    input.setAttribute('readonly', '')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(input)
    return ok
  } catch {
    return false
  }
}

/** Pokémon TCG card: 63×88mm. Pack as many as will fit on A4 with cut gutters. */
const PRINT_CARD_W_MM = 63
const PRINT_CARD_H_MM = 88
const PRINT_GAP_MM = 2.5
const PRINT_MARGIN_MM = 8
const PRINT_PAGE_W_MM = 210
const PRINT_PAGE_H_MM = 297

function printGridCols() {
  const usable = PRINT_PAGE_W_MM - PRINT_MARGIN_MM * 2
  return Math.max(1, Math.floor((usable + PRINT_GAP_MM) / (PRINT_CARD_W_MM + PRINT_GAP_MM)))
}

function printGridRows() {
  const usable = PRINT_PAGE_H_MM - PRINT_MARGIN_MM * 2
  return Math.max(1, Math.floor((usable + PRINT_GAP_MM) / (PRINT_CARD_H_MM + PRINT_GAP_MM)))
}

function chunkItems(items, size) {
  const pages = []
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size))
  }
  return pages
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function collectBinderPrintItems(binder, { content = 'all', scope = 'all', currentPageId = null } = {}) {
  const pages = Array.isArray(binder?.pages) ? binder.pages : []
  const scoped =
    scope === 'current' ? pages.filter((p) => p.id === currentPageId) : pages

  const items = []
  scoped.forEach((page) => {
    ;(page.slots || []).forEach((slot) => {
      if (slot?.covered) return
      const hasCard = !!slot?.card?.imageUrl
      const hasCustom = !!slot?.customImage?.url
      if (content === 'catalogue' && !hasCard) return
      if (content === 'uploads' && !hasCustom) return
      if (content === 'all' && !hasCard && !hasCustom) return

      const url = hasCard ? slot.card.imageUrl : slot.customImage.url
      const name = hasCard
        ? slot.card.name || 'Card'
        : 'Uploaded image'
      items.push({
        url,
        name,
        custom: !hasCard && hasCustom,
        numberLabel: hasCard ? slot.card.numberLabel || '' : '',
        colSpan: Number(slot.colSpan) === 2 ? 2 : 1,
      })
    })
  })
  return items
}

function collectBinderPrintPages(binder, { content = 'all', scope = 'all', currentPageId = null } = {}) {
  const pages = Array.isArray(binder?.pages) ? binder.pages : []
  const scoped =
    scope === 'current' ? pages.filter((p) => p.id === currentPageId) : pages

  return scoped.map((page) => {
    const slots = (page.slots || [])
      .filter((slot) => !slot?.covered)
      .map((slot) => {
        const hasCard = !!slot?.card?.imageUrl
        const hasCustom = !!slot?.customImage?.url
        let url = ''
        let name = ''
        let custom = false
        let include = false
        if (content === 'catalogue' && hasCard) include = true
        else if (content === 'uploads' && hasCustom) include = true
        else if (content === 'all' && (hasCard || hasCustom)) include = true

        if (include) {
          url = hasCard ? slot.card.imageUrl : slot.customImage.url
          name = hasCard ? slot.card.name || 'Card' : 'Uploaded image'
          custom = !hasCard && hasCustom
        }

        return {
          slotIndex: slot.slotIndex,
          colSpan: Number(slot.colSpan) === 2 ? 2 : 1,
          url,
          name,
          custom,
          filled: include,
        }
      })

    return {
      id: page.id,
      label: page.label || 'Page',
      slots,
    }
  })
}

function packPrintItemsIntoSheets(items, cols, rows) {
  const sheets = []
  let grid = null

  const newSheet = () => {
    grid = Array.from({ length: rows }, () => Array(cols).fill(false))
    sheets.push([])
  }

  const findSpot = (span) => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c <= cols - span; c++) {
        let ok = true
        for (let k = 0; k < span; k++) {
          if (grid[r][c + k]) {
            ok = false
            break
          }
        }
        if (ok) return [r, c]
      }
    }
    return null
  }

  const occupy = (r, c, span) => {
    for (let k = 0; k < span; k++) grid[r][c + k] = true
  }

  newSheet()
  items.forEach((item) => {
    const span = Math.min(item.colSpan === 2 ? 2 : 1, cols)
    let spot = findSpot(span)
    if (!spot) {
      newSheet()
      spot = findSpot(span)
    }
    if (!spot) return
    occupy(spot[0], spot[1], span)
    sheets[sheets.length - 1].push({
      ...item,
      colSpan: span,
      gridRow: spot[0] + 1,
      gridCol: spot[1] + 1,
    })
  })

  return sheets.filter((sheet) => sheet.length)
}

function printToolbarHtml(titleHtml, helpHtml) {
  return `<div class="print-toolbar">
    <p><strong>${titleHtml}</strong> — ${helpHtml}</p>
    <div class="print-toolbar__actions">
      <button type="button" class="print-toolbar__print" onclick="window.print()">Print</button>
      <button type="button" class="print-toolbar__close" onclick="window.close()">Close</button>
    </div>
  </div>`
}

function printAutoPrintScript() {
  return `<script>
    (function () {
      var images = Array.prototype.slice.call(document.images || []);
      if (!images.length) return;
      var pending = images.length;
      var done = false;
      function maybePrint() {
        if (done) return;
        done = true;
        setTimeout(function () { window.print(); }, 250);
      }
      function tick() {
        pending -= 1;
        if (pending <= 0) maybePrint();
      }
      images.forEach(function (img) {
        if (img.complete) tick();
        else {
          img.addEventListener('load', tick);
          img.addEventListener('error', tick);
        }
      });
      setTimeout(maybePrint, 8000);
    })();
  </script>`
}

function buildBinderPrintDocument({ title, items, showCutGuides = true, showLabels = false }) {
  const cols = printGridCols()
  const rows = printGridRows()
  const sheets = packPrintItemsIntoSheets(items, cols, rows)
  const binderTitle = escapeHtml(title || 'Binder')

  const sheetsHtml = sheets
    .map((sheet, sheetIndex) => {
      const cards = sheet
        .map((item) => {
          const span = item.colSpan === 2 ? 2 : 1
          const label = showLabels
            ? `<span class="print-card__label">${escapeHtml(item.name)}${
                item.numberLabel ? ` · ${escapeHtml(item.numberLabel)}` : ''
              }</span>`
            : ''
          return `<article class="print-card${item.custom ? ' is-custom' : ''}${
            span === 2 ? ' is-michi' : ''
          }${showLabels ? ' has-label' : ''}" style="grid-row:${item.gridRow};grid-column:${item.gridCol} / span ${span};">
  <img src="${escapeHtml(item.url)}" alt="${escapeHtml(item.name)}" />
  ${label}
</article>`
        })
        .join('\n')

      return `<section class="print-sheet" aria-label="Print sheet ${sheetIndex + 1}">
  ${cards}
</section>`
    })
    .join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Print · ${binderTitle}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Syne:wght@700;800&display=swap" rel="stylesheet">
<style>
  @page {
    size: A4 portrait;
    margin: ${PRINT_MARGIN_MM}mm;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
  }

  .print-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1.1rem;
    background: #111;
    color: #f5f5f5;
  }

  .print-toolbar p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.35;
    max-width: 40rem;
  }

  .print-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .print-toolbar button {
    appearance: none;
    border: 0;
    border-radius: 0.5rem;
    padding: 0.55rem 0.9rem;
    font: inherit;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .print-toolbar__print {
    background: #f5b942;
    color: #111;
  }

  .print-toolbar__close {
    background: #333;
    color: #f5f5f5;
  }

  .print-meta {
    padding: 0.75rem 1.1rem 0;
    font-size: 0.8rem;
    color: #555;
  }

  .print-sheet {
    display: grid;
    grid-template-columns: repeat(${cols}, ${PRINT_CARD_W_MM}mm);
    grid-template-rows: repeat(${rows}, ${PRINT_CARD_H_MM}mm);
    gap: ${PRINT_GAP_MM}mm;
    justify-content: center;
    align-content: start;
    width: ${PRINT_PAGE_W_MM - PRINT_MARGIN_MM * 2}mm;
    min-height: ${PRINT_PAGE_H_MM - PRINT_MARGIN_MM * 2}mm;
    margin: 0 auto;
    padding: 0;
    page-break-after: always;
    break-after: page;
  }

  .print-sheet:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .print-card {
    position: relative;
    width: ${PRINT_CARD_W_MM}mm;
    height: ${PRINT_CARD_H_MM}mm;
    overflow: hidden;
    background: #fff;
    border-radius: 2.5mm;
    ${showCutGuides ? `box-shadow: 0 0 0 0.15mm #888;` : 'box-shadow: none;'}
  }

  .print-card.is-michi {
    width: ${PRINT_CARD_W_MM * 2 + PRINT_GAP_MM}mm;
  }

  .print-card.has-label {
    height: calc(${PRINT_CARD_H_MM}mm + 6mm);
  }

  .print-card img {
    display: block;
    width: 100%;
    height: ${PRINT_CARD_H_MM}mm;
    object-fit: cover;
    object-position: center;
    border-radius: 2.5mm;
  }

  .print-card:not(.is-custom) img {
    filter: grayscale(1) contrast(1.05);
  }

  .print-card:not(.is-custom)::before {
    content: "PROXY";
    position: absolute;
    left: 50%;
    top: 42%;
    transform: translate(-50%, -50%) rotate(-28deg);
    font-family: Syne, ui-sans-serif, system-ui, sans-serif;
    font-weight: 800;
    font-size: 7mm;
    letter-spacing: 0.08em;
    color: rgba(0, 0, 0, 0.18);
    pointer-events: none;
    z-index: 1;
  }

  .print-card__label {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 0.8mm 1.2mm;
    font-size: 2.2mm;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: #fff;
  }

  @media print {
    .print-toolbar,
    .print-meta {
      display: none !important;
    }

    .print-sheet {
      margin: 0 auto;
    }

    .print-card.has-label {
      height: ${PRINT_CARD_H_MM}mm;
    }

    .print-card__label {
      display: none;
    }
  }
</style>
</head>
<body>
  ${printToolbarHtml(
    binderTitle,
    `cards at Pokémon size (${PRINT_CARD_W_MM}×${PRINT_CARD_H_MM}mm; Michi ${PRINT_CARD_W_MM * 2}×${PRINT_CARD_H_MM}mm), packed on A4. Use actual size / 100% scale when printing.`,
  )}
  <p class="print-meta">${sheets.length} sheet${sheets.length === 1 ? '' : 's'}${
    showCutGuides ? ' · cut guides on' : ''
  }</p>
  ${sheetsHtml}
  ${printAutoPrintScript()}
</body>
</html>`
}

function buildBinderPagePrintDocument({ title, binder, pages }) {
  const binderTitle = escapeHtml(title || 'Binder')
  const cols = Math.max(1, Number(binder?.cols) || 1)
  const rows = Math.max(1, Number(binder?.rows) || 1)
  const pageW = Math.min(
    PRINT_PAGE_W_MM - PRINT_MARGIN_MM * 2,
    cols * PRINT_CARD_W_MM + (cols - 1) * PRINT_GAP_MM,
  )
  const cellW = (pageW - (cols - 1) * PRINT_GAP_MM) / cols
  const cellH = (cellW * PRINT_CARD_H_MM) / PRINT_CARD_W_MM

  const sheetsHtml = pages
    .map((page, pageIndex) => {
      const pockets = (page.slots || [])
        .map((slot) => {
          const span = slot.colSpan === 2 ? 2 : 1
          const img = slot.filled
            ? `<img src="${escapeHtml(slot.url)}" alt="${escapeHtml(slot.name)}" />`
            : ''
          return `<article class="print-pocket${slot.filled ? ' is-filled' : ''}${
            slot.custom ? ' is-custom' : ''
          }${span === 2 ? ' is-michi' : ''}" style="grid-column: span ${span};">
  ${img}
</article>`
        })
        .join('\n')

      return `<section class="print-binder-page" aria-label="${escapeHtml(page.label || `Page ${pageIndex + 1}`)}">
  <p class="print-binder-page__label">${escapeHtml(page.label || `Page ${pageIndex + 1}`)}</p>
  <div class="print-binder-page__grid">
  ${pockets}
  </div>
</section>`
    })
    .join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Print pages · ${binderTitle}</title>
<style>
  @page {
    size: A4 portrait;
    margin: ${PRINT_MARGIN_MM}mm;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif;
  }

  .print-toolbar {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1.1rem;
    background: #111;
    color: #f5f5f5;
  }

  .print-toolbar p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.35;
    max-width: 40rem;
  }

  .print-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .print-toolbar button {
    appearance: none;
    border: 0;
    border-radius: 0.5rem;
    padding: 0.55rem 0.9rem;
    font: inherit;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .print-toolbar__print {
    background: #f5b942;
    color: #111;
  }

  .print-toolbar__close {
    background: #333;
    color: #f5f5f5;
  }

  .print-meta {
    padding: 0.75rem 1.1rem 0;
    font-size: 0.8rem;
    color: #555;
  }

  .print-binder-page {
    width: ${pageW}mm;
    margin: 0 auto ${PRINT_MARGIN_MM}mm;
    page-break-after: always;
    break-after: page;
  }

  .print-binder-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .print-binder-page__label {
    margin: 0 0 2mm;
    font-size: 3mm;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #666;
  }

  .print-binder-page__grid {
    display: grid;
    grid-template-columns: repeat(${cols}, ${cellW}mm);
    grid-auto-rows: ${cellH}mm;
    gap: ${PRINT_GAP_MM}mm;
  }

  .print-pocket {
    position: relative;
    overflow: hidden;
    background: #f6f6f6;
    border: 0.2mm solid #ddd;
    border-radius: 1.5mm;
  }

  .print-pocket img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
  }

  .print-pocket:not(.is-custom).is-filled img {
    filter: grayscale(1) contrast(1.05);
  }

  @media print {
    .print-toolbar,
    .print-meta,
    .print-binder-page__label {
      display: none !important;
    }

    .print-binder-page {
      margin: 0 auto;
    }
  }
</style>
</head>
<body>
  ${printToolbarHtml(
    binderTitle,
    `binder page layout (${rows}×${cols}), one sheet per page. Use actual size / 100% scale when printing.`,
  )}
  <p class="print-meta">${pages.length} binder page${pages.length === 1 ? '' : 's'}</p>
  ${sheetsHtml}
  ${printAutoPrintScript()}
</body>
</html>`
}

function openBinderPrintWindow(options) {
  const mode = options.mode === 'pages' ? 'pages' : 'cards'
  const win = window.open('', '_blank')
  if (!win) {
    throw new Error('Allow pop-ups to open the print preview.')
  }

  let html
  if (mode === 'pages') {
    const pages = options.pages || []
    if (!pages.length) {
      win.close()
      throw new Error('No binder pages match those print options.')
    }
    html = buildBinderPagePrintDocument(options)
  } else {
    const items = options.items || []
    if (!items.length) {
      win.close()
      throw new Error('No cards match those print options.')
    }
    html = buildBinderPrintDocument(options)
  }

  win.document.open()
  win.document.write(html)
  win.document.close()
  return win
}

export function createBinderIndex(config) {
  const bindersMeta = Array.isArray(config.bindersMeta) ? config.bindersMeta : []
  const favoritedById = {}
  const galleryStatusById = {}
  bindersMeta.forEach((b) => {
    favoritedById[b.id] = Boolean(b.favorited)
    if (b.gallerySubmission?.status) galleryStatusById[b.id] = b.gallerySubmission.status
  })

  return {
    createOpen: false,
    creating: false,
    createError: '',
    binderCount: Number(config.binderCount) || 0,
    favoritedById,
    galleryStatusById,
    layoutPresets: config.layoutPresets || [],
    sourceOptions: config.sourceOptions || [],
    coverColorPresets: config.coverColorPresets || [],
    lists: config.lists || [],
    sets: config.sets || [],
    regions: config.regions || [],
    setFilter: '',
    form: {
      name: '',
      rows: 3,
      cols: 3,
      importCards: false,
      defaultSource: 'set',
      setupSetName: '',
      setupListId: '',
      fillSetOrder: 'desc',
      sourceUrl: '',
      setupRegion: '',
      includeMegaVariants: false,
      initialPages: 1,
      coverColor: '#1a1e27',
    },

    galleryStatusLabel(status) {
      if (status === 'pending') return '· Awaiting gallery review'
      if (status === 'approved') return '· In gallery'
      if (status === 'removal_pending') return '· Removal pending'
      if (status === 'rejected') return '· Gallery declined'
      if (status === 'removed') return '· Removed from gallery'
      return ''
    },

    galleryActionLabel(status) {
      if (status === 'pending') return 'Awaiting review'
      if (status === 'approved') return 'Remove from gallery'
      if (status === 'removal_pending') return 'Removal pending'
      return 'Submit to gallery'
    },

    galleryActionDisabled(status) {
      return status === 'pending' || status === 'removal_pending'
    },

    async galleryButtonAction(id, name, filledSlots) {
      const status = this.galleryStatusById[id]
      if (status === 'approved') {
        await this.requestGalleryRemoval(id, name)
        return
      }
      if (status === 'pending' || status === 'removal_pending') {
        showSiteToast(
          status === 'removal_pending'
            ? 'Removal is already awaiting review.'
            : 'This binder is already awaiting review.',
        )
        return
      }
      await this.submitToGallery(id, name, filledSlots)
    },

    async toggleFavorite(id) {
      if (!config.toggleFavoriteUrl) return
      const next = !this.favoritedById[id]
      this.favoritedById[id] = next
      const el = document.querySelector(`[data-binder-id="${id}"]`)
      el?.classList.toggle('is-favorited', next)
      el?.setAttribute('data-favorited', next ? '1' : '0')
      try {
        await postJson(
          config.toggleFavoriteUrl,
          { binderId: id, favorited: next ? '1' : '0' },
          config.csrfName,
          config.csrfToken,
        )
        showSiteToast(next ? 'Added to favorites.' : 'Removed from favorites.')
      } catch (err) {
        this.favoritedById[id] = !next
        el?.classList.toggle('is-favorited', !next)
        el?.setAttribute('data-favorited', !next ? '1' : '0')
        showSiteToast(err.message || 'Couldn’t update favorite.')
      }
    },

    async submitToGallery(id, name, filledSlots) {
      if (!config.submitToGalleryUrl) return
      if (this.galleryStatusById[id] === 'pending') {
        showSiteToast('This binder is already awaiting review.')
        return
      }
      if (this.galleryStatusById[id] === 'approved' || this.galleryStatusById[id] === 'removal_pending') {
        showSiteToast('This binder is already in the gallery.')
        return
      }
      if ((Number(filledSlots) || 0) < 1) {
        showSiteToast('Add at least one card before submitting.')
        return
      }

      const message = await showPrompt({
        title: 'Submit to gallery?',
        message: `Send a frozen snapshot of “${name}” to the Chase team. Editing the binder later won’t change what you submit.`,
        eyebrow: 'Public gallery',
        label: 'Note (optional)',
        placeholder: 'Anything the team should know…',
        initialValue: '',
        confirmLabel: 'Submit',
        maxLength: 1000,
      })
      if (message == null) return

      try {
        const data = await postJson(
          config.submitToGalleryUrl,
          { binderId: id, message: String(message).trim() },
          config.csrfName,
          config.csrfToken,
        )
        if (data.submission?.status) {
          this.galleryStatusById[id] = data.submission.status
        }
        showSiteToast('Submitted for review — thanks!')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t submit binder.')
      }
    },

    async requestGalleryRemoval(id, name) {
      if (!config.requestGalleryRemovalUrl) return
      if (this.galleryStatusById[id] === 'removal_pending') {
        showSiteToast('Removal is already awaiting review.')
        return
      }
      if (this.galleryStatusById[id] !== 'approved') {
        showSiteToast('This binder is not in the public gallery.')
        return
      }

      const ok = await showConfirm({
        title: 'Remove from gallery?',
        message: `Request that “${name}” be taken down from the public gallery? It stays listed until the Chase team approves the removal.`,
        eyebrow: 'Public gallery',
        confirmLabel: 'Request removal',
        cancelLabel: 'Keep listed',
      })
      if (!ok) return

      try {
        const data = await postJson(
          config.requestGalleryRemovalUrl,
          { binderId: id },
          config.csrfName,
          config.csrfToken,
        )
        if (data.submission?.status) {
          this.galleryStatusById[id] = data.submission.status
        }
        showSiteToast('Removal requested — awaiting review.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t request removal.')
      }
    },

    get importSourceOptions() {
      return [
        ...this.sourceOptions,
        {
          key: 'tcgcollector',
          label: 'TCG Collector',
          hint: 'Paste a share link to bulk-fill pockets in order.',
        },
        {
          key: 'region',
          label: 'Pokémon regions',
          hint: 'One random card per Pokémon in the region — gaps for ones you don’t have.',
        },
      ]
    },

    get filteredSets() {
      return filterByQuery(this.sets, this.setFilter, ['name', 'rawSetName'])
    },

    get selectedSet() {
      const name = this.form.setupSetName
      if (!name) return null
      return (
        this.sets.find((s) => (s.rawSetName ?? s.name) === name || s.name === name) || null
      )
    },

    get selectedList() {
      const id = Number(this.form.setupListId)
      if (!id) return null
      return this.lists.find((l) => l.id === id) || null
    },

    get selectedRegion() {
      const key = this.form.setupRegion
      if (!key) return null
      return this.regions.find((r) => r.key === key) || null
    },

    get selectedSourceCardCount() {
      if (this.form.defaultSource === 'set') {
        return Number(this.selectedSet?.cardCount) || 0
      }
      if (this.form.defaultSource === 'list') {
        return Number(this.selectedList?.cardCount) || 0
      }
      if (this.form.defaultSource === 'region') {
        const region = this.selectedRegion
        if (!region) return 0
        return Number(this.form.includeMegaVariants ? region.totalCount : region.speciesCount) || 0
      }
      return 0
    },

    get pagesFromSelectedSource() {
      const count = this.selectedSourceCardCount
      const pockets = Math.max(1, (Number(this.form.rows) || 1) * (Number(this.form.cols) || 1))
      if (count < 1) return 1
      return Math.min(50, Math.max(1, Math.ceil(count / pockets)))
    },

    get pagesFieldLocked() {
      return (
        this.form.importCards &&
        (this.form.defaultSource === 'set' ||
          this.form.defaultSource === 'list' ||
          this.form.defaultSource === 'region')
      )
    },

    syncPagesFromSet() {
      this.form.initialPages = this.pagesFieldLocked ? this.pagesFromSelectedSource : 1
    },

    openCreate() {
      this.createError = ''
      this.createOpen = true
      this.syncPagesFromSet()
      window.app?.disableScroll?.()
    },

    closeCreate() {
      this.createOpen = false
      window.app?.enableScroll?.()
    },

    async submitCreate() {
      this.creating = true
      this.createError = ''

      if (this.form.importCards && this.form.defaultSource === 'set' && !this.form.setupSetName) {
        this.createError = 'Choose a set.'
        this.creating = false
        return
      }
      if (this.form.importCards && this.form.defaultSource === 'list' && !this.form.setupListId) {
        this.createError = this.lists.length ? 'Choose a list.' : 'Create a list on My Chases first.'
        this.creating = false
        return
      }
      if (this.form.importCards && this.form.defaultSource === 'tcgcollector' && !this.form.sourceUrl) {
        this.createError = 'Paste a TCG Collector share URL.'
        this.creating = false
        return
      }
      if (this.form.importCards && this.form.defaultSource === 'region' && !this.form.setupRegion) {
        this.createError = 'Choose a region.'
        this.creating = false
        return
      }

      this.syncPagesFromSet()

      const useSource = this.form.importCards ? this.form.defaultSource : 'set'
      const regionTotal = useSource === 'region' ? this.selectedSourceCardCount : 0

      try {
        const data = await postJson(
          config.createUrl,
          {
            name: this.form.name,
            rows: this.form.rows,
            cols: this.form.cols,
            defaultSource: useSource,
            initialPages: this.form.initialPages,
            setupSetName: this.form.importCards && useSource === 'set' ? this.form.setupSetName : '',
            setupListId: this.form.importCards && useSource === 'list' ? this.form.setupListId : '',
            fillSetOrder: this.form.importCards && useSource === 'set' ? this.form.fillSetOrder : '',
            sourceUrl: this.form.importCards && useSource === 'tcgcollector' ? this.form.sourceUrl : '',
            setupRegion: this.form.importCards && useSource === 'region' ? this.form.setupRegion : '',
            includeMegaVariants:
              this.form.importCards && useSource === 'region' ? this.form.includeMegaVariants : false,
            coverColor: this.form.coverColor,
          },
          config.csrfName,
          config.csrfToken,
        )
        if (data.filling) {
          showSiteToast('Binder created — filling from TCG Collector…')
        } else if (data.filledFromSet > 0) {
          showSiteToast(
            `Binder created — placed ${data.filledFromSet} card${data.filledFromSet === 1 ? '' : 's'} from the set.`,
          )
        } else if (data.filledFromList > 0) {
          showSiteToast(
            `Binder created — placed ${data.filledFromList} card${data.filledFromList === 1 ? '' : 's'} from the list.`,
          )
        } else if (data.filledFromRegion > 0) {
          const gaps = Math.max(0, regionTotal - data.filledFromRegion)
          showSiteToast(
            `Binder created — placed ${data.filledFromRegion} card${data.filledFromRegion === 1 ? '' : 's'} from the region` +
              (gaps > 0 ? `, ${gaps} gap${gaps === 1 ? '' : 's'} left for cards you don’t have.` : '.'),
          )
        }
        window.location.href = data.editUrl || `/account/binders/${data.binderId}`
      } catch (err) {
        this.createError = err.message || 'Couldn’t create binder.'
        this.creating = false
      }
    },

    async renameBinder(id, currentName) {
      const name = await showPrompt({
        title: 'Rename binder',
        message: 'Choose a new name for this binder.',
        eyebrow: 'Binders',
        initialValue: currentName,
        confirmLabel: 'Rename',
      })
      if (name == null || !String(name).trim()) return
      try {
        await postJson(
          config.renameUrl,
          { binderId: id, name: String(name).trim() },
          config.csrfName,
          config.csrfToken,
        )
        showSiteToast('Binder renamed.')
        window.location.reload()
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t rename binder.')
      }
    },

    async deleteBinder(id, name) {
      const ok = await showConfirm({
        title: 'Delete binder?',
        message: `Delete “${name}”? This can’t be undone.`,
        confirmLabel: 'Delete',
        danger: true,
      })
      if (!ok) return
      try {
        await postJson(
          config.deleteUrl,
          { binderId: id },
          config.csrfName,
          config.csrfToken,
        )
        document.querySelector(`[data-binder-id="${id}"]`)?.remove()
        this.binderCount = Math.max(0, this.binderCount - 1)
        showSiteToast('Binder deleted.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t delete binder.')
      }
    },

    async copyShareUrl(shareUrl) {
      const ok = await copyText(shareUrl)
      showSiteToast(ok ? 'Share link copied.' : 'Couldn’t copy link — try again.')
      if (ok) {
        window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'sharedBinderUrl' } }))
      }
    },
  }
}

export function createBinderEditor(config) {
  const binder = structuredClone(config.binder)
  if (!binder.coverColor) binder.coverColor = '#1a1e27'

  return {
    binder,
    urls: config.urls,
    layoutPresets: config.layoutPresets || [],
    coverColorPresets: config.coverColorPresets || [],
    pageViewModeOptions: config.pageViewModeOptions || [],
    sourceOptions: config.sourceOptions || [],
    lists: config.lists || [],
    settingsSets: config.sets || [],
    regions: config.regions || [],
    currentPageId: config.binder.pages?.[0]?.id ?? null,
    pageViewMode: binder.pageViewMode === 'single' ? 'single' : 'spread',
    spreadIndex: 0,
    busy: false,
    shareCopied: false,
    moveFrom: null,
    dragFrom: null,
    dragOverPageId: null,
    dragOverSlotIndex: null,
    suppressNextPocketClick: false,
    slotMenuOpen: false,
    activeSlot: null,
    activeSlotPageId: null,

    get activeSlotPage() {
      if (!this.activeSlotPageId) return this.currentPage
      return this.binder.pages.find((p) => p.id === this.activeSlotPageId) || this.currentPage
    },
    alertPrice: '',
    slotMatches: [],
    slotMatchesLoading: false,
    slotMatchesError: '',
    slotMatchesListingsUrl: '',
    slotMatchesRequestId: 0,
    settingsOpen: false,
    settingsBusy: false,
    settingsError: '',
    fillBusy: false,
    setFilter: '',
    settingsForm: {
      name: binder.name || '',
      rows: binder.rows || 3,
      cols: binder.cols || 3,
      coverColor: binder.coverColor || '#1a1e27',
      pageViewMode: binder.pageViewMode === 'single' ? 'single' : 'spread',
      defaultSource: binder.defaultSource || 'set',
      setupSetName: binder.setupSetName || '',
      setupListId: binder.setupListId || '',
      fillSetOrder: 'desc',
      sourceUrl: '',
      setupRegion: '',
      includeMegaVariants: false,
    },
    pickerOpen: false,
    pickerStep: 'root',
    pickerLoading: false,
    pickerError: '',
    pickerFilter: '',
    pickerPlaceMode: 'one',
    selectedPickerCardIds: [],
    sets: [],
    pickerCards: [],
    pokedexEntries: [],
    pokedexLoading: false,
    pokedexLoadingMore: false,
    pokedexSearch: '',
    pokedexPage: 0,
    pokedexHasMore: false,
    pokedexTotal: 0,
    _pokedexSearchTimer: null,
    selectedSet: null,
    selectedPokemon: null,
    selectedListName: '',
    printOpen: false,
    printBusy: false,
    printForm: {
      mode: 'cards',
      content: 'all',
      scope: 'all',
      cutGuides: true,
    },

    get currentPage() {
      return this.binder.pages.find((p) => p.id === this.currentPageId) || this.binder.pages[0] || null
    },

    get currentSlots() {
      return this.currentPage?.slots || []
    },

    visibleSlots(slots) {
      return (slots || []).filter((slot) => !slot?.covered)
    },

    isMichiSlot(slot) {
      return Number(slot?.colSpan) === 2
    },

    /** Michi (2-wide) is only for user-uploaded images — not catalogue cards or empty pockets. */
    isUploadSlot(slot) {
      return !!slot?.customImage && !slot?.card
    },

    canOfferMichi(slot, page) {
      return !!page && this.isUploadSlot(slot) && !this.isMichiSlot(slot) && !slot?.covered
    },

    canMergeLeft(slot, page) {
      if (!this.canOfferMichi(slot, page)) return false
      const cols = Number(this.binder.cols) || 1
      if (cols < 2) return false
      if (slot.slotIndex % cols === 0) return false
      const prev = (page.slots || []).find((s) => s.slotIndex === slot.slotIndex - 1)
      if (!prev || prev.covered || this.isMichiSlot(prev)) return false
      // Neighbor must be empty so the upload can expand into it
      if (this.isSlotFilled(prev)) return false
      return true
    },

    canMergeRight(slot, page) {
      if (!this.canOfferMichi(slot, page)) return false
      const cols = Number(this.binder.cols) || 1
      if (cols < 2) return false
      if (slot.slotIndex % cols === cols - 1) return false
      const next = (page.slots || []).find((s) => s.slotIndex === slot.slotIndex + 1)
      if (!next || next.covered || this.isMichiSlot(next)) return false
      if (this.isSlotFilled(next)) return false
      return true
    },

    mergeEdgeDisabled(slot, direction) {
      const cols = Number(this.binder.cols) || 1
      if (!slot || cols < 2) return true
      if (direction === 'left') return slot.slotIndex % cols === 0
      return slot.slotIndex % cols === cols - 1
    },

    get currentPageIndex() {
      return this.binder.pages.findIndex((p) => p.id === this.currentPageId)
    },

    get canPrevPage() {
      return this.currentPageIndex > 0
    },

    get canNextPage() {
      return this.currentPageIndex >= 0 && this.currentPageIndex < this.binder.pages.length - 1
    },

    get pagePositionLabel() {
      const total = this.binder.pages.length
      if (!total) return 'No pages'
      const idx = this.currentPageIndex
      return `${idx >= 0 ? idx + 1 : 1} / ${total}`
    },

    // Spread ("2 pages") view — mirrors a real binder: page 1 sits alone on
    // the right (the left is the blank inside-cover), then pages pair up
    // (2,3) (4,5) …, so an even page count leaves the last page alone on
    // the left with the right pocket blank.
    spreadIndexForPageIndex(idx) {
      return idx <= 0 ? 0 : Math.ceil(idx / 2)
    },

    get spreadCount() {
      const total = this.binder.pages.length
      if (total <= 1) return 1
      return 1 + Math.ceil((total - 1) / 2)
    },

    get spreadLeftPage() {
      if (this.spreadIndex <= 0) return null
      return this.binder.pages[this.spreadIndex * 2 - 1] || null
    },

    get spreadRightPage() {
      const idx = this.spreadIndex <= 0 ? 0 : this.spreadIndex * 2
      return this.binder.pages[idx] || null
    },

    get spreadLeftSlots() {
      return this.spreadLeftPage?.slots || this.emptySlots()
    },

    get spreadRightSlots() {
      return this.spreadRightPage?.slots || this.emptySlots()
    },

    get spreadLeftFolio() {
      return this.spreadLeftPage ? this.spreadLeftPage.label : ''
    },

    get spreadRightFolio() {
      return this.spreadRightPage ? this.spreadRightPage.label : ''
    },

    get canPrevSpread() {
      return this.spreadIndex > 0
    },

    get canNextSpread() {
      return this.spreadIndex < this.spreadCount - 1
    },

    get canGoPrev() {
      return this.pageViewMode === 'spread' ? this.canPrevSpread : this.canPrevPage
    },

    get canGoNext() {
      return this.pageViewMode === 'spread' ? this.canNextSpread : this.canNextPage
    },

    get navPositionLabel() {
      if (this.pageViewMode !== 'spread') return this.pagePositionLabel
      if (!this.binder.pages.length) return 'No pages'
      return `${this.spreadIndex + 1} / ${this.spreadCount}`
    },

    emptySlots() {
      const n = (this.binder.rows || 1) * (this.binder.cols || 1)
      return Array.from({ length: n }, (_, i) => ({
        slotIndex: i,
        colSpan: 1,
        covered: false,
        card: null,
        customImage: null,
      }))
    },

    setPageViewMode(mode) {
      if (mode !== 'single' && mode !== 'spread') return
      if (this.pageViewMode === mode) return
      this.pageViewMode = mode
      if (mode === 'spread') {
        this.spreadIndex = this.spreadIndexForPageIndex(Math.max(0, this.currentPageIndex))
      }
    },

    syncCurrentPageFromSpread() {
      const page = this.spreadRightPage || this.spreadLeftPage
      if (page) this.currentPageId = page.id
    },

    prevSpread() {
      if (!this.canPrevSpread) return
      this.spreadIndex -= 1
      this.syncCurrentPageFromSpread()
    },

    nextSpread() {
      if (!this.canNextSpread) return
      this.spreadIndex += 1
      this.syncCurrentPageFromSpread()
    },

    goPrev() {
      if (this.pageViewMode === 'spread') this.prevSpread()
      else this.prevPage()
    },

    goNext() {
      if (this.pageViewMode === 'spread') this.nextSpread()
      else this.nextPage()
    },

    get sheetStyle() {
      return {
        '--binder-rows': String(this.binder.rows),
        '--binder-cols': String(this.binder.cols),
        '--binder-cover': this.binder.coverColor || '#1a1e27',
      }
    },

    get filteredSets() {
      return filterByQuery(this.sets, this.pickerFilter, ['name', 'code'])
    },

    get filteredSettingsSets() {
      return filterByQuery(this.settingsSets, this.setFilter, ['name', 'rawSetName'])
    },

    get editSourceOptions() {
      return [
        ...this.sourceOptions,
        {
          key: 'tcgcollector',
          label: 'TCG Collector',
          hint: 'Paste a share link to bulk-fill empty pockets.',
        },
        {
          key: 'region',
          label: 'Pokémon regions',
          hint: 'One random card per Pokémon in the region — gaps for ones you don’t have.',
        },
      ]
    },

    get selectedSettingsRegion() {
      const key = this.settingsForm.setupRegion
      if (!key) return null
      return this.regions.find((r) => r.key === key) || null
    },

    get fillActionLabel() {
      const source = this.settingsForm.defaultSource
      if (source === 'tcgcollector') return 'Start import'
      if (source === 'region') return 'Fill empty pockets'
      if (source === 'set') return 'Fill empty pockets from set'
      if (source === 'list') return 'Fill empty pockets from list'
      return 'Fill empty pockets'
    },

    get filteredPickerCards() {
      return filterByQuery(this.pickerCards, this.pickerFilter, [
        'name',
        'numberLabel',
        'setName',
        'category',
      ])
    },

    get pickerShowingCards() {
      return (
        this.pickerStep === 'set-cards' ||
        this.pickerStep === 'chase' ||
        this.pickerStep === 'list-cards' ||
        this.pickerStep === 'pokedex-cards'
      )
    },

    get selectedPickerCount() {
      return this.selectedPickerCardIds.length
    },

    get printItems() {
      return collectBinderPrintItems(this.binder, {
        content: this.printForm.content,
        scope: this.printForm.scope,
        currentPageId: this.currentPageId,
      })
    },

    get printPages() {
      return collectBinderPrintPages(this.binder, {
        content: this.printForm.content,
        scope: this.printForm.scope,
        currentPageId: this.currentPageId,
      })
    },

    get printCanRun() {
      if (this.printForm.mode === 'pages') {
        return this.printPages.some((page) =>
          (page.slots || []).some((slot) => slot.filled),
        )
      }
      return this.printItems.length > 0
    },

    get printPreviewLabel() {
      if (this.printForm.mode === 'pages') {
        const pages = this.printPages
        const filled = pages.reduce(
          (sum, page) => sum + (page.slots || []).filter((s) => s.filled).length,
          0,
        )
        if (!filled) return 'No cards match these options.'
        return `${filled} pocket${filled === 1 ? '' : 's'} · ${pages.length} binder page sheet${
          pages.length === 1 ? '' : 's'
        } · ${this.binder.rows}×${this.binder.cols} layout`
      }
      const count = this.printItems.length
      if (!count) return 'No cards match these options.'
      const cols = printGridCols()
      const rows = printGridRows()
      const sheets = packPrintItemsIntoSheets(this.printItems, cols, rows).length
      const michi = this.printItems.filter((i) => i.colSpan === 2).length
      return `${count} card${count === 1 ? '' : 's'}${
        michi ? ` (${michi} Michi)` : ''
      } · ~${sheets} A4 sheet${sheets === 1 ? '' : 's'} · ${cols}×${rows} grid`
    },

    isSlotFilled(slot) {
      return !!(slot?.card || slot?.customImage)
    },

    slotImageUrl(slot) {
      return slot?.card?.imageUrl || slot?.customImage?.url || ''
    },

    syncSettingsFormFromBinder() {
      this.settingsForm = {
        name: this.binder.name || '',
        rows: this.binder.rows || 3,
        cols: this.binder.cols || 3,
        coverColor: this.binder.coverColor || '#1a1e27',
        pageViewMode: this.binder.pageViewMode === 'single' ? 'single' : 'spread',
        defaultSource: this.binder.defaultSource || 'set',
        setupSetName: this.binder.setupSetName || '',
        setupListId: this.binder.setupListId || '',
        fillSetOrder: 'desc',
        sourceUrl: '',
        setupRegion: '',
        includeMegaVariants: false,
      }
      this.setFilter = ''
      this.settingsError = ''
    },

    openSettings() {
      if (this.slotMenuOpen) this.closeSlotMenu()
      if (this.printOpen) this.closePrintModal()
      this.syncSettingsFormFromBinder()
      this.settingsBusy = false
      this.settingsOpen = true
      window.app?.disableScroll?.()
    },

    closeSettings() {
      this.settingsOpen = false
      this.settingsBusy = false
      this.settingsError = ''
      window.app?.enableScroll?.()
    },

    openPrintModal() {
      if (this.slotMenuOpen) this.closeSlotMenu()
      if (this.settingsOpen) this.closeSettings()
      this.printBusy = false
      this.printOpen = true
      window.app?.disableScroll?.()
    },

    closePrintModal() {
      this.printOpen = false
      this.printBusy = false
      window.app?.enableScroll?.()
    },

    runPrint() {
      if (this.printBusy || !this.printCanRun) return
      this.printBusy = true
      try {
        if (this.printForm.mode === 'pages') {
          openBinderPrintWindow({
            mode: 'pages',
            title: this.binder?.name || 'Binder',
            binder: this.binder,
            pages: this.printPages,
          })
        } else {
          openBinderPrintWindow({
            mode: 'cards',
            title: this.binder?.name || 'Binder',
            items: this.printItems,
            showCutGuides: !!this.printForm.cutGuides,
            showLabels: false,
          })
        }
        this.closePrintModal()
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t open print preview.')
        this.printBusy = false
      }
    },

    async copyShareUrl() {
      const url = this.urls?.share || this.binder?.shareUrl
      const ok = await copyText(url)
      if (!ok) {
        showSiteToast('Couldn’t copy link — try again.')
        return
      }
      this.shareCopied = true
      showSiteToast('Share link copied.')
      window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'sharedBinderUrl' } }))
      window.setTimeout(() => {
        this.shareCopied = false
      }, 2000)
    },

    async toggleFavorite() {
      if (!this.urls.toggleFavorite || this.busy) return
      const next = !this.binder.favorited
      this.binder.favorited = next
      try {
        await postJson(
          this.urls.toggleFavorite,
          { binderId: this.binder.id, favorited: next ? '1' : '0' },
          config.csrfName,
          config.csrfToken,
        )
        showSiteToast(next ? 'Added to favorites.' : 'Removed from favorites.')
      } catch (err) {
        this.binder.favorited = !next
        showSiteToast(err.message || 'Couldn’t update favorite.')
      }
    },

    async submitToGallery() {
      if (!this.urls.submitToGallery || this.busy) return
      const status = this.binder.gallerySubmission?.status
      if (status === 'pending') {
        showSiteToast('This binder is already awaiting review.')
        return
      }
      if (status === 'approved' || status === 'removal_pending') {
        showSiteToast('This binder is already in the gallery.')
        return
      }
      const filled = (this.binder.pages || []).reduce((sum, page) => {
        return (
          sum +
          (page.slots || []).filter((s) => s.card || s.customImage).length
        )
      }, 0)
      if (filled < 1) {
        showSiteToast('Add at least one card before submitting.')
        return
      }

      const message = await showPrompt({
        title: 'Submit to gallery?',
        message:
          'We’ll freeze a snapshot of this binder for the Chase team to review. Later edits won’t change what you submit.',
        eyebrow: 'Public gallery',
        label: 'Note (optional)',
        placeholder: 'Anything the team should know…',
        initialValue: '',
        confirmLabel: 'Submit',
        maxLength: 1000,
      })
      if (message == null) return

      this.busy = true
      try {
        const data = await postJson(
          this.urls.submitToGallery,
          { binderId: this.binder.id, message: String(message).trim() },
          config.csrfName,
          config.csrfToken,
        )
        if (data.binder) this.applyBinder(data.binder)
        showSiteToast('Submitted for review — thanks!')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t submit binder.')
      } finally {
        this.busy = false
      }
    },

    galleryActionLabel(status) {
      if (status === 'pending') return 'Awaiting gallery review'
      if (status === 'approved') return 'Remove from gallery'
      if (status === 'removal_pending') return 'Removal pending'
      return 'Submit to gallery'
    },

    galleryActionDisabled(status) {
      return status === 'pending' || status === 'removal_pending'
    },

    async galleryButtonAction() {
      const status = this.binder.gallerySubmission?.status
      if (status === 'approved') {
        await this.requestGalleryRemoval()
        return
      }
      if (status === 'pending' || status === 'removal_pending') {
        showSiteToast(
          status === 'removal_pending'
            ? 'Removal is already awaiting review.'
            : 'This binder is already awaiting review.',
        )
        return
      }
      await this.submitToGallery()
    },

    async requestGalleryRemoval() {
      if (!this.urls.requestGalleryRemoval || this.busy) return
      const status = this.binder.gallerySubmission?.status
      if (status === 'removal_pending') {
        showSiteToast('Removal is already awaiting review.')
        return
      }
      if (status !== 'approved') {
        showSiteToast('This binder is not in the public gallery.')
        return
      }

      const ok = await showConfirm({
        title: 'Remove from gallery?',
        message:
          'Request that this binder be taken down from the public gallery? It stays listed until the Chase team approves the removal.',
        eyebrow: 'Public gallery',
        confirmLabel: 'Request removal',
        cancelLabel: 'Keep listed',
      })
      if (!ok) return

      this.busy = true
      try {
        const data = await postJson(
          this.urls.requestGalleryRemoval,
          { binderId: this.binder.id },
          config.csrfName,
          config.csrfToken,
        )
        if (data.binder) this.applyBinder(data.binder)
        showSiteToast('Removal requested — awaiting review.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t request removal.')
      } finally {
        this.busy = false
      }
    },

    selectPage(pageId) {
      this.currentPageId = pageId
      if (this.pageViewMode === 'spread') {
        const idx = this.binder.pages.findIndex((p) => p.id === pageId)
        this.spreadIndex = this.spreadIndexForPageIndex(Math.max(0, idx))
      }
    },

    prevPage() {
      if (!this.canPrevPage) return
      const prev = this.binder.pages[this.currentPageIndex - 1]
      if (prev) this.currentPageId = prev.id
    },

    nextPage() {
      if (!this.canNextPage) return
      const next = this.binder.pages[this.currentPageIndex + 1]
      if (next) this.currentPageId = next.id
    },

    async submitSettings() {
      const form = this.settingsForm
      const name = String(form.name || '').trim()
      if (!name) {
        this.settingsError = 'Enter a binder name.'
        return
      }

      const color = String(form.coverColor || '').trim()
      if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) {
        this.settingsError = 'Enter a valid hex colour (e.g. #1a1e27).'
        return
      }

      const layoutChanged =
        Number(form.rows) !== Number(this.binder.rows) ||
        Number(form.cols) !== Number(this.binder.cols)

      if (layoutChanged) {
        const preset = this.layoutPresets.find(
          (p) => p.rows === Number(form.rows) && p.cols === Number(form.cols),
        )
        const label = preset?.label || `${form.rows}×${form.cols}`
        const ok = await showConfirm({
          title: 'Change page size?',
          message: `Switch to ${label}? Cards stay in order and reflow into the new pockets.`,
          confirmLabel: 'Save & change size',
          danger: false,
        })
        if (!ok) return
      }

      this.settingsBusy = true
      this.settingsError = ''
      this.busy = true

      const pageViewMode = form.pageViewMode === 'single' ? 'single' : 'spread'
      const isPersistableSource =
        form.defaultSource === 'set' || form.defaultSource === 'list' || form.defaultSource === 'pokedex'

      try {
        const data = await postJson(
          this.urls.updateSettings,
          {
            binderId: this.binder.id,
            name,
            coverColor: color,
            pageViewMode,
            defaultSource: isPersistableSource ? form.defaultSource : undefined,
            setupSetName: isPersistableSource && form.defaultSource === 'set' ? form.setupSetName : '',
            setupListId: isPersistableSource && form.defaultSource === 'list' ? form.setupListId : '',
          },
          config.csrfName,
          config.csrfToken,
        )

        if (data.binder) {
          this.applyBinder(data.binder)
        } else {
          this.binder.name = name
          this.binder.coverColor = color
          this.binder.pageViewMode = pageViewMode
        }
        this.setPageViewMode(this.binder.pageViewMode === 'single' ? 'single' : 'spread')

        if (layoutChanged) {
          const layoutData = await postJson(
            this.urls.changeLayout,
            {
              binderId: this.binder.id,
              rows: Number(form.rows),
              cols: Number(form.cols),
            },
            config.csrfName,
            config.csrfToken,
          )
          this.applyBinder(layoutData.binder)
        }

        this.closeSettings()
        showSiteToast('Binder updated.')
      } catch (err) {
        this.settingsError = err.message || 'Couldn’t save binder settings.'
        showSiteToast(this.settingsError)
      } finally {
        this.settingsBusy = false
        this.busy = false
      }
    },

    async fillEmptyPockets() {
      const form = this.settingsForm
      const source = form.defaultSource
      if (source === 'pokedex') return

      if (source === 'set' && !form.setupSetName) {
        this.settingsError = 'Choose a set.'
        return
      }
      if (source === 'list' && !form.setupListId) {
        this.settingsError = this.lists.length ? 'Choose a list.' : 'Create a list on My Chases first.'
        return
      }
      if (source === 'tcgcollector' && !form.sourceUrl) {
        this.settingsError = 'Paste a TCG Collector share URL.'
        return
      }
      if (source === 'region' && !form.setupRegion) {
        this.settingsError = 'Choose a region.'
        return
      }

      this.settingsError = ''
      this.fillBusy = true
      this.busy = true

      try {
        if (source === 'set') {
          const data = await postJson(
            this.urls.fillFromSet,
            { binderId: this.binder.id, setName: form.setupSetName, fillSetOrder: form.fillSetOrder },
            config.csrfName,
            config.csrfToken,
          )
          this.applyBinder(data.binder)
          showSiteToast(
            data.filled > 0
              ? `Placed ${data.filled} card${data.filled === 1 ? '' : 's'} from the set.`
              : 'No empty pockets to fill.',
          )
        } else if (source === 'list') {
          const data = await postJson(
            this.urls.fillFromList,
            { binderId: this.binder.id, listId: form.setupListId },
            config.csrfName,
            config.csrfToken,
          )
          this.applyBinder(data.binder)
          showSiteToast(
            data.filled > 0
              ? `Placed ${data.filled} card${data.filled === 1 ? '' : 's'} from the list.`
              : 'No empty pockets to fill.',
          )
        } else if (source === 'tcgcollector') {
          const data = await postJson(
            this.urls.fillFromShareUrl,
            { binderId: this.binder.id, sourceUrl: form.sourceUrl },
            config.csrfName,
            config.csrfToken,
          )
          showSiteToast(data.message || 'Import started — refresh in a minute to see new cards.')
        } else if (source === 'region') {
          const region = this.selectedSettingsRegion
          const total = region ? (form.includeMegaVariants ? region.totalCount : region.speciesCount) : 0
          const data = await postJson(
            this.urls.fillFromRegion,
            { binderId: this.binder.id, region: form.setupRegion, includeMegaVariants: form.includeMegaVariants },
            config.csrfName,
            config.csrfToken,
          )
          this.applyBinder(data.binder)
          const gaps = Math.max(0, total - data.filled)
          showSiteToast(
            data.filled > 0
              ? `Placed ${data.filled} card${data.filled === 1 ? '' : 's'} from the region` +
                  (gaps > 0 ? `, ${gaps} gap${gaps === 1 ? '' : 's'} left for cards you don’t have.` : '.')
              : 'No cards found for that region yet.',
          )
        }
      } catch (err) {
        this.settingsError = err.message || 'Couldn’t fill pockets.'
        showSiteToast(this.settingsError)
      } finally {
        this.fillBusy = false
        this.busy = false
      }
    },

    async changeLayout(key) {
      const preset = this.layoutPresets.find((p) => p.key === key)
      if (!preset) return
      if (preset.rows === this.binder.rows && preset.cols === this.binder.cols) return

      const ok = await showConfirm({
        title: 'Change page size?',
        message: `Switch to ${preset.label}? Cards stay in order and reflow into the new pockets.`,
        confirmLabel: 'Change size',
        danger: false,
      })
      if (!ok) return

      this.busy = true
      try {
        const data = await postJson(
          this.urls.changeLayout,
          {
            binderId: this.binder.id,
            rows: preset.rows,
            cols: preset.cols,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        showSiteToast(`Page size set to ${preset.label}.`)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t change page size.')
      } finally {
        this.busy = false
      }
    },

    onPocketClick(slot, page) {
      if (this.suppressNextPocketClick) {
        this.suppressNextPocketClick = false
        return
      }
      if (this.moveFrom) {
        this.finishMove(slot, page)
        return
      }
      if (!this.isSlotFilled(slot)) return
      this.activeSlot = slot
      this.activeSlotPageId = page?.id ?? this.currentPageId
      const max = slot.card?.alertMaxPrice
      this.alertPrice = max != null && max !== '' ? String(max) : ''
      this.resetSlotMatches()
      this.slotMenuOpen = true
      window.app?.disableScroll?.()
      this.loadSlotMatches()
    },

    onEmptyPocketClick(slot, page) {
      if (this.moveFrom) {
        this.finishMove(slot, page)
      }
    },

    onPocketDragStart(slot, page, event) {
      if (this.busy || this.moveFrom || !this.isSlotFilled(slot) || !page) {
        event.preventDefault()
        return
      }
      this.dragFrom = {
        pageId: page.id,
        slotIndex: slot.slotIndex,
      }
      this.dragOverPageId = null
      this.dragOverSlotIndex = null
      this.suppressNextPocketClick = false
      const dt = event.dataTransfer
      if (dt) {
        dt.effectAllowed = 'move'
        dt.setData('text/plain', String(slot.slotIndex))
        const img = event.currentTarget?.querySelector?.('img')
        if (img) {
          try {
            dt.setDragImage(img, img.clientWidth / 2, img.clientHeight / 2)
          } catch {
            // ignore
          }
        }
      }
    },

    onPocketDragOver(slot, page, event) {
      if (!this.dragFrom || this.busy) return
      if (
        this.dragFrom.pageId === page?.id &&
        this.dragFrom.slotIndex === slot.slotIndex
      ) {
        return
      }
      event.dataTransfer.dropEffect = 'move'
      this.dragOverPageId = page?.id ?? null
      this.dragOverSlotIndex = slot.slotIndex
    },

    onPocketDragLeave(slot, page, event) {
      if (this.dragOverPageId !== (page?.id ?? null) || this.dragOverSlotIndex !== slot.slotIndex) return
      const related = event.relatedTarget
      if (related && event.currentTarget?.contains?.(related)) return
      this.dragOverPageId = null
      this.dragOverSlotIndex = null
    },

    async onPocketDrop(slot, page, event) {
      if (!this.dragFrom || !page || this.busy) return
      if (
        this.dragFrom.pageId === page.id &&
        this.dragFrom.slotIndex === slot.slotIndex
      ) {
        this.dragFrom = null
        this.dragOverPageId = null
        this.dragOverSlotIndex = null
        return
      }
      this.suppressNextPocketClick = true
      this.moveFrom = { ...this.dragFrom }
      this.dragFrom = null
      this.dragOverPageId = null
      this.dragOverSlotIndex = null
      await this.finishMove(slot, page)
    },

    onPocketDragEnd() {
      this.dragFrom = null
      this.dragOverPageId = null
      this.dragOverSlotIndex = null
      // Click often fires after a drag; ignore that one.
      this.suppressNextPocketClick = true
      window.setTimeout(() => {
        this.suppressNextPocketClick = false
      }, 0)
    },

    closeSlotMenu() {
      this.slotMenuOpen = false
      this.resetSlotMatches()
      window.app?.enableScroll?.()
    },

    resetSlotMatches() {
      this.slotMatchesRequestId += 1
      this.slotMatches = []
      this.slotMatchesLoading = false
      this.slotMatchesError = ''
      this.slotMatchesListingsUrl = ''
    },

    async loadSlotMatches() {
      const card = this.activeSlot?.card
      const userCardId = Number(card?.userCardId)
      if (!card?.inChases || !userCardId || !this.urls.recentMatches) {
        this.slotMatches = []
        this.slotMatchesLoading = false
        this.slotMatchesError = ''
        return
      }
      if (!card.alertsEnabled) {
        this.slotMatches = []
        this.slotMatchesLoading = false
        this.slotMatchesError = ''
        this.slotMatchesListingsUrl = ''
        return
      }

      const requestId = ++this.slotMatchesRequestId
      this.slotMatchesLoading = true
      this.slotMatchesError = ''
      this.slotMatches = []

      try {
        const url = new URL(this.urls.recentMatches, window.location.origin)
        url.searchParams.set('userCardId', String(userCardId))
        const data = await getJson(url.toString())
        if (requestId !== this.slotMatchesRequestId) return
        this.slotMatches = Array.isArray(data.matches) ? data.matches : []
        this.slotMatchesListingsUrl = data.listingsUrl || ''
      } catch (err) {
        if (requestId !== this.slotMatchesRequestId) return
        this.slotMatchesError = err.message || 'Couldn’t load Vinted results.'
        this.slotMatches = []
      } finally {
        if (requestId === this.slotMatchesRequestId) {
          this.slotMatchesLoading = false
        }
      }
    },

    startPlaceFor(slot, page) {
      if (this.isMichiSlot(slot)) {
        showSiteToast('Michi pockets are for uploaded images only.')
        return
      }
      this.activeSlot = slot
      this.activeSlotPageId = page?.id ?? this.currentPageId
      this.openPicker()
    },

    startPlace() {
      if (this.isMichiSlot(this.activeSlot)) {
        showSiteToast('Michi pockets are for uploaded images only.')
        return
      }
      this.closeSlotMenu()
      this.openPicker()
    },

    startMove() {
      if (!this.isSlotFilled(this.activeSlot) || !this.activeSlotPageId) return
      this.moveFrom = {
        pageId: this.activeSlotPageId,
        slotIndex: this.activeSlot.slotIndex,
      }
      this.closeSlotMenu()
      showSiteToast('Click a pocket to move the item.')
    },

    async uploadFor(slot, page, event) {
      const input = event?.target
      const file = input?.files?.[0]
      if (input) input.value = ''
      if (!file || !page) return
      await this.uploadSlotImage(page.id, slot.slotIndex, file)
    },

    async uploadActiveSlot(event) {
      const input = event?.target
      const file = input?.files?.[0]
      if (input) input.value = ''
      if (!file || this.activeSlot?.slotIndex == null || !this.activeSlotPageId) return
      this.closeSlotMenu()
      await this.uploadSlotImage(this.activeSlotPageId, this.activeSlot.slotIndex, file)
    },

    async uploadSlotImage(pageId, slotIndex, file) {
      if (!pageId || !this.urls.uploadImage) {
        showSiteToast('Upload isn’t available.')
        return
      }
      this.busy = true
      try {
        const body = new FormData()
        body.append(config.csrfName, config.csrfToken)
        body.append('binderId', String(this.binder.id))
        body.append('pageId', String(pageId))
        body.append('slotIndex', String(slotIndex))
        body.append('image', file)

        const res = await fetch(this.urls.uploadImage, {
          method: 'POST',
          body,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || data.success === false) {
          throw new Error(data.error || `Upload failed (${res.status})`)
        }
        this.applyBinder(data.binder)
        showSiteToast('Image uploaded to pocket.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t upload image.')
      } finally {
        this.busy = false
      }
    },

    cancelMove() {
      this.moveFrom = null
    },

    async finishMove(slot, page) {
      if (!this.moveFrom || !page) return
      if (
        this.moveFrom.pageId === page.id &&
        this.moveFrom.slotIndex === slot.slotIndex
      ) {
        this.moveFrom = null
        return
      }
      const destFilled = this.isSlotFilled(slot)
      this.busy = true
      try {
        const data = await postJson(
          this.urls.moveSlot,
          {
            binderId: this.binder.id,
            fromPageId: this.moveFrom.pageId,
            fromSlotIndex: this.moveFrom.slotIndex,
            toPageId: page.id,
            toSlotIndex: slot.slotIndex,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        this.moveFrom = null
        showSiteToast(destFilled ? 'Cards swapped.' : 'Card moved.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t move card.')
      } finally {
        this.busy = false
      }
    },

    async clearActiveSlot() {
      if (!this.activeSlot || !this.activeSlotPageId) return
      this.busy = true
      try {
        const data = await postJson(
          this.urls.clearSlot,
          {
            binderId: this.binder.id,
            pageId: this.activeSlotPageId,
            slotIndex: this.activeSlot.slotIndex,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        this.closeSlotMenu()
        showSiteToast('Pocket cleared.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t clear pocket.')
      } finally {
        this.busy = false
      }
    },

    async mergePocket(slot, page, direction = 'right') {
      if (!slot || !page || this.busy) return
      if (!this.isUploadSlot(slot)) {
        showSiteToast('Michi is only available on uploaded images.')
        return
      }

      const pageId = page.id
      const slotIndex = slot.slotIndex
      const dir = direction === 'left' ? 'left' : 'right'
      const pageNow = this.binder.pages.find((p) => p.id === pageId)
      const slotNow = pageNow?.slots?.find((s) => s.slotIndex === slotIndex)
      if (!pageNow || !slotNow) return

      if (dir === 'left' ? !this.canMergeLeft(slotNow, pageNow) : !this.canMergeRight(slotNow, pageNow)) {
        if (this.mergeEdgeDisabled(slotNow, dir)) {
          showSiteToast(
            dir === 'left'
              ? 'This pocket is already at the left edge of the row.'
              : 'This pocket is already at the right edge of the row.',
          )
        } else {
          showSiteToast('Clear the neighboring pocket before making a Michi slot.')
        }
        return
      }

      // Close the pocket modal first so confirm isn’t stacked under it / Escape isn’t shared.
      const reopen = this.slotMenuOpen
      if (reopen) this.closeSlotMenu()

      const ok = await showConfirm({
        title: dir === 'left' ? 'Michi left?' : 'Michi right?',
        message: `Expand this uploaded image into a 2-wide landscape pocket to the ${dir}.`,
        confirmLabel: dir === 'left' ? 'Michi left' : 'Michi right',
        danger: false,
      })
      if (!ok) {
        if (reopen) {
          const p = this.binder.pages.find((pg) => pg.id === pageId)
          const s = p?.slots?.find((sl) => sl.slotIndex === slotIndex)
          if (p && s) this.onPocketClick(s, p)
        }
        return
      }

      this.busy = true
      try {
        const data = await postJson(
          this.urls.mergeSlot,
          {
            binderId: this.binder.id,
            pageId,
            slotIndex,
            direction: dir,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        showSiteToast('Michi pocket created.')
        // Re-open on the merged (anchor) pocket so Split stays available.
        const anchorIndex = dir === 'left' ? slotIndex - 1 : slotIndex
        const p = this.binder.pages.find((pg) => pg.id === pageId)
        const s = p?.slots?.find((sl) => sl.slotIndex === anchorIndex)
        if (p && s) this.onPocketClick(s, p)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t create Michi pocket.')
        if (reopen) {
          const p = this.binder.pages.find((pg) => pg.id === pageId)
          const s = p?.slots?.find((sl) => sl.slotIndex === slotIndex)
          if (p && s) this.onPocketClick(s, p)
        }
      } finally {
        this.busy = false
      }
    },

    async splitPocket(slot, page) {
      if (!slot || !page || this.busy || !this.isMichiSlot(slot)) return
      const pageId = page.id
      const slotIndex = slot.slotIndex

      const reopen = this.slotMenuOpen
      if (reopen) this.closeSlotMenu()

      const ok = await showConfirm({
        title: 'Split Michi pocket?',
        message: 'Turn this 2-wide pocket back into two normal pockets. Any uploaded image stays in the left pocket.',
        confirmLabel: 'Split',
        danger: false,
      })
      if (!ok) {
        if (reopen) {
          const p = this.binder.pages.find((pg) => pg.id === pageId)
          const s = p?.slots?.find((sl) => sl.slotIndex === slotIndex)
          if (p && s) this.onPocketClick(s, p)
        }
        return
      }

      this.busy = true
      try {
        const data = await postJson(
          this.urls.splitSlot,
          {
            binderId: this.binder.id,
            pageId,
            slotIndex,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        showSiteToast('Split into two pockets.')
        const p = this.binder.pages.find((pg) => pg.id === pageId)
        const s = p?.slots?.find((sl) => sl.slotIndex === slotIndex)
        if (p && s && this.isSlotFilled(s)) this.onPocketClick(s, p)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t split pocket.')
        if (reopen) {
          const p = this.binder.pages.find((pg) => pg.id === pageId)
          const s = p?.slots?.find((sl) => sl.slotIndex === slotIndex)
          if (p && s) this.onPocketClick(s, p)
        }
      } finally {
        this.busy = false
      }
    },

    applyBinder(next) {
      if (!next) return
      const keepPageId = this.currentPageId
      const keepSlotPageId = this.activeSlotPageId
      const keepSlotIndex = this.activeSlot?.slotIndex
      if (!next.coverColor) {
        next.coverColor = this.binder.coverColor || '#1a1e27'
      }
      this.binder = next
      if (!this.binder.pages.some((p) => p.id === keepPageId)) {
        this.currentPageId = this.binder.pages[0]?.id ?? null
      } else {
        this.currentPageId = keepPageId
      }
      if (this.slotMenuOpen && keepSlotIndex != null && keepSlotPageId != null) {
        const page = this.binder.pages.find((p) => p.id === keepSlotPageId)
        this.activeSlot = page?.slots?.find((s) => s.slotIndex === keepSlotIndex) || null
        if (!this.activeSlot) {
          this.closeSlotMenu()
        }
      }
      if (this.pageViewMode === 'spread') {
        const maxIdx = Math.max(0, this.spreadCount - 1)
        if (this.spreadIndex > maxIdx) this.spreadIndex = maxIdx
      }
    },

    patchChaseOnCard(cardId, chase) {
      const id = Number(cardId)
      if (!id || !chase) return
      this.binder.pages.forEach((page) => {
        page.slots.forEach((slot) => {
          if (slot.card?.id === id) {
            Object.assign(slot.card, chase)
          }
        })
      })
      if (this.activeSlot?.card?.id === id) {
        Object.assign(this.activeSlot.card, chase)
      }
    },

    async addActiveSlotToChases() {
      const card = this.activeSlot?.card
      if (!card?.id || card.inChases || this.busy || !this.urls.addToChases) return

      let userCardId = 0
      this.busy = true
      try {
        const data = await postJson(
          this.urls.addToChases,
          { cardId: card.id },
          config.csrfName,
          config.csrfToken,
        )

        userCardId = Number(data.userCardId) || 0
        this.patchChaseOnCard(card.id, {
          inChases: true,
          userCardId: userCardId || null,
          chaseUrl: data.chaseUrl || (userCardId ? `/account/cards/${userCardId}` : null),
          alertsEnabled: false,
          alertFavorited: false,
          alertMaxPrice: null,
          lists: [],
        })
        showSiteToast(data.message || `Added ${card.name} to My Chases.`)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t add to My Chases.')
        return
      } finally {
        this.busy = false
      }

      if (!userCardId) return

      const wantsList = await showConfirm({
        title: 'Add to a list?',
        message: `Also put “${card.name}” on a list?`,
        confirmLabel: 'Add to list',
        cancelLabel: 'Not now',
        danger: false,
      })
      if (!wantsList) return

      this.busy = true
      try {
        await this.assignListsForChase(card, userCardId)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t update lists.')
      } finally {
        this.busy = false
      }
    },

    async ensureActiveSlotUserCardId() {
      const card = this.activeSlot?.card
      if (!card?.id) return null
      if (card.userCardId) return Number(card.userCardId)

      if (!this.urls.addToChases) {
        showSiteToast('Add to My Chases isn’t available.')
        return null
      }

      const data = await postJson(
        this.urls.addToChases,
        { cardId: card.id },
        config.csrfName,
        config.csrfToken,
      )
      const userCardId = Number(data.userCardId) || 0
      if (!userCardId) {
        showSiteToast('Couldn’t link this card to My Chases.')
        return null
      }

      this.patchChaseOnCard(card.id, {
        inChases: true,
        userCardId,
        chaseUrl: data.chaseUrl || `/account/cards/${userCardId}`,
        alertsEnabled: Boolean(card.alertsEnabled),
        alertFavorited: Boolean(card.alertFavorited),
        alertMaxPrice: card.alertMaxPrice ?? null,
        lists: Array.isArray(card.lists) ? card.lists : [],
      })
      return userCardId
    },

    async enableAlertForActiveSlot() {
      const card = this.activeSlot?.card
      if (!card?.id) {
        showSiteToast('No card selected.')
        return
      }
      if (this.busy) return
      if (!this.urls.toggleAlert) {
        showSiteToast('Alert action isn’t available.')
        return
      }

      const price = Number(String(this.alertPrice || '').trim())
      if (!Number.isFinite(price) || price <= 0) {
        showSiteToast('Enter a max price greater than £0.')
        return
      }

      this.busy = true
      try {
        const userCardId = await this.ensureActiveSlotUserCardId()
        if (!userCardId) return

        const data = await postJson(
          this.urls.toggleAlert,
          {
            userCardId,
            enabled: '1',
            maxPrice: price.toFixed(2),
          },
          config.csrfName,
          config.csrfToken,
        )
        const nextPrice = data.maxPrice != null ? Number(data.maxPrice) : price
        this.patchChaseOnCard(card.id, {
          inChases: true,
          userCardId,
          alertsEnabled: true,
          alertMaxPrice: nextPrice,
        })
        this.alertPrice = nextPrice.toFixed(2)
        showSiteToast(data.message || 'Alert added.')
        window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'setFirstAlert' } }))
        await this.loadSlotMatches()
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t set alert.')
      } finally {
        this.busy = false
      }
    },

    async disableAlertForActiveSlot() {
      const card = this.activeSlot?.card
      if (!card?.id || this.busy) return
      if (!this.urls.toggleAlert) {
        showSiteToast('Alert action isn’t available.')
        return
      }

      const ok = await showConfirm({
        title: 'Disable this alert?',
        message: `Stop watching Vinted for “${card.name}”? You can turn it back on later.`,
        confirmLabel: 'Disable',
        cancelLabel: 'Cancel',
        danger: true,
      })
      if (!ok) return

      this.busy = true
      try {
        const userCardId = await this.ensureActiveSlotUserCardId()
        if (!userCardId) return

        const data = await postJson(
          this.urls.toggleAlert,
          {
            userCardId,
            enabled: '0',
          },
          config.csrfName,
          config.csrfToken,
        )
        this.patchChaseOnCard(card.id, {
          alertsEnabled: false,
        })
        this.resetSlotMatches()
        showSiteToast(data.message || 'Alert disabled.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t disable alert.')
      } finally {
        this.busy = false
      }
    },

    async assignListsForChase(card, userCardId) {
      let listIds = []

      if (this.lists.length > 0) {
        const useExisting = await showConfirm({
          title: 'Existing or new list?',
          message: `Add “${card.name}” to lists you already have, or create a new list with this card in it?`,
          confirmLabel: 'Existing lists',
          cancelLabel: 'Create new list',
          danger: false,
        })

        if (useExisting) {
          const picked = await showChecklist({
            eyebrow: 'Lists',
            title: 'Choose lists',
            message: `Select one or more lists for “${card.name}”.`,
            label: 'Your lists',
            items: this.lists.map((list) => ({
              value: String(list.id),
              label: list.name,
            })),
            confirmLabel: 'Add to lists',
            cancelLabel: 'Cancel',
          })
          if (picked === null) return
          listIds = picked.map((v) => Number(v)).filter((id) => id > 0)
          if (listIds.length === 0) {
            showSiteToast('No lists selected.')
            return
          }
        } else {
          const createdId = await this.createListForChase(card.name)
          if (!createdId) return
          listIds = [createdId]
        }
      } else {
        const createdId = await this.createListForChase(card.name)
        if (!createdId) return
        listIds = [createdId]
      }

      await this.setListMembership(userCardId, listIds)

      const selectedLists = this.lists.filter((l) => listIds.includes(l.id))
      this.patchChaseOnCard(card.id, { lists: selectedLists.map((l) => ({ id: l.id, name: l.name })) })
      showSiteToast(
        selectedLists.length === 1
          ? `Added to “${selectedLists[0].name}”.`
          : `Added to ${selectedLists.length} lists.`,
      )
    },

    async createListForChase(cardName) {
      const name = await showPrompt({
        eyebrow: 'Lists',
        title: 'Create a new list',
        message: `Name a list for “${cardName}”.`,
        label: 'List name',
        placeholder: 'e.g. Binder chase',
        confirmLabel: 'Create list',
        cancelLabel: 'Cancel',
      })
      if (name == null || !String(name).trim()) return null

      const data = await postJson(
        this.urls.createList,
        { name: String(name).trim() },
        config.csrfName,
        config.csrfToken,
      )
      if (!data.list?.id) {
        throw new Error(data.error || 'Couldn’t create list.')
      }
      const list = {
        id: Number(data.list.id),
        name: String(data.list.name || name),
        favorited: Boolean(data.list.favorited),
        cardCount: 0,
      }
      this.lists = [...this.lists, list]
      return list.id
    },

    async setListMembership(userCardId, listIds) {
      const body = new FormData()
      body.append(config.csrfName, config.csrfToken)
      body.append('userCardId', String(userCardId))
      listIds.forEach((id) => body.append('listIds[]', String(id)))

      const res = await fetch(this.urls.setMembership, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `Request failed (${res.status})`)
      }
      return data
    },

    async addPage() {
      this.busy = true
      try {
        const data = await postJson(
          this.urls.addPage,
          { binderId: this.binder.id },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        if (data.pageId) {
          this.currentPageId = data.pageId
          if (this.pageViewMode === 'spread') {
            const idx = this.binder.pages.findIndex((p) => p.id === data.pageId)
            this.spreadIndex = this.spreadIndexForPageIndex(Math.max(0, idx))
          }
        }
        showSiteToast('Page added.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t add page.')
      } finally {
        this.busy = false
      }
    },

    async deleteCurrentPage() {
      if (!this.currentPage || this.binder.pages.length <= 1) return
      const ok = await showConfirm({
        title: 'Remove page?',
        message: `Remove ${this.currentPage.label}? Cards on this page will be cleared.`,
        confirmLabel: 'Remove',
        danger: true,
      })
      if (!ok) return
      this.busy = true
      try {
        const data = await postJson(
          this.urls.deletePage,
          { binderId: this.binder.id, pageId: this.currentPage.id },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        showSiteToast('Page removed.')
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t remove page.')
      } finally {
        this.busy = false
      }
    },

    openPicker() {
      if (this.settingsOpen) this.closeSettings()
      this.pickerOpen = true
      this.pickerError = ''
      this.pickerFilter = ''
      this.pickerCards = []
      this.pokedexSearch = ''
      this.selectedPickerCardIds = []
      this.pickerPlaceMode = 'one'
      this.pickerStep = 'root'
      window.app?.disableScroll?.()
    },

    closePicker() {
      this.pickerOpen = false
      this.selectedPickerCardIds = []
      if (this._pokedexSearchTimer) {
        clearTimeout(this._pokedexSearchTimer)
        this._pokedexSearchTimer = null
      }
      window.app?.enableScroll?.()
    },

    setPickerPlaceMode(mode) {
      if (mode !== 'one' && mode !== 'several') return
      this.pickerPlaceMode = mode
      if (mode === 'one') {
        this.selectedPickerCardIds = []
      }
    },

    clearPickerSelection() {
      this.selectedPickerCardIds = []
    },

    isPickerCardSelected(card) {
      return this.selectedPickerCardIds.includes(card.id)
    },

    togglePickerCard(card) {
      if (!card?.id) return
      const idx = this.selectedPickerCardIds.indexOf(card.id)
      if (idx >= 0) {
        this.selectedPickerCardIds = this.selectedPickerCardIds.filter((id) => id !== card.id)
      } else {
        this.selectedPickerCardIds = [...this.selectedPickerCardIds, card.id]
      }
    },

    onPickerCardClick(card) {
      if (this.pickerPlaceMode === 'several') {
        this.togglePickerCard(card)
        return
      }
      this.placeCard(card)
    },

    openCatalogue() {
      this.pickerStep = 'sets'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.loadSets()
    },

    async openChase() {
      this.pickerStep = 'chase'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.pickerLoading = true
      this.pickerError = ''
      try {
        const data = await getJson(this.urls.chaseCards)
        this.pickerCards = data.cards || []
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load chases.'
        this.pickerCards = []
      } finally {
        this.pickerLoading = false
      }
    },

    openLists() {
      this.pickerStep = 'lists'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
    },

    async openListCards(list) {
      this.selectedListName = list.name
      this.pickerStep = 'list-cards'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.pickerLoading = true
      this.pickerError = ''
      try {
        const data = await getJson(withParams(this.urls.listCards, { listId: list.id }))
        this.pickerCards = data.cards || []
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load list cards.'
        this.pickerCards = []
      } finally {
        this.pickerLoading = false
      }
    },

    async openPokedex() {
      this.pickerStep = 'pokedex'
      this.pickerFilter = ''
      this.pokedexSearch = ''
      this.selectedPickerCardIds = []
      this.pickerError = ''
      await this.loadPokedex({ reset: true })
    },

    backToPokedex() {
      this.pickerStep = 'pokedex'
      this.selectedPokemon = null
      this.pickerCards = []
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.pickerError = ''
      this.afterPaint(() => this.fillPokedexIfNeeded())
    },

    onPokedexSearchInput() {
      if (this._pokedexSearchTimer) {
        clearTimeout(this._pokedexSearchTimer)
      }
      this._pokedexSearchTimer = setTimeout(() => {
        this.loadPokedex({ reset: true })
      }, 280)
    },

    afterPaint(fn) {
      return new Promise((resolve) => {
        const done = () => {
          try {
            fn?.()
          } finally {
            resolve()
          }
        }
        if (typeof this.$nextTick === 'function') {
          this.$nextTick(done)
        } else {
          requestAnimationFrame(() => requestAnimationFrame(done))
        }
      })
    },

    pickerScrollEl() {
      return this.$refs?.pickerBody || this.$el?.querySelector?.('.desk-picker__body') || null
    },

    onPickerBodyScroll() {
      if (this.pickerStep !== 'pokedex') return
      if (!this.pokedexHasMore || this.pokedexLoading || this.pokedexLoadingMore) return

      const el = this.pickerScrollEl()
      if (!el) return

      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
      if (remaining < 320) {
        this.loadPokedex()
      }
    },

    /** Keep fetching while the list doesn’t fill the panel. */
    fillPokedexIfNeeded() {
      if (this.pickerStep !== 'pokedex') return
      if (!this.pokedexHasMore || this.pokedexLoading || this.pokedexLoadingMore) return

      const el = this.pickerScrollEl()
      if (!el) return

      if (el.scrollHeight <= el.clientHeight + 40) {
        this.loadPokedex()
      }
    },

    async loadPokedex({ reset = false } = {}) {
      if (!this.urls.libraryPokedex) {
        this.pickerError = 'Picker config missing.'
        return
      }

      if (reset) {
        this.pokedexEntries = []
        this.pokedexPage = 0
        this.pokedexHasMore = true
        this.pokedexTotal = 0
        const el = this.pickerScrollEl()
        if (el) el.scrollTop = 0
      }

      if (!this.pokedexHasMore && !reset) return
      if (this.pokedexLoading || this.pokedexLoadingMore) return

      const nextPage = this.pokedexPage + 1
      const isFirst = nextPage === 1
      if (isFirst) {
        this.pokedexLoading = true
      } else {
        this.pokedexLoadingMore = true
      }
      this.pickerError = ''

      try {
        const params = { page: nextPage }
        const q = this.pokedexSearch.trim()
        if (q) params.q = q

        const data = await getJson(withParams(this.urls.libraryPokedex, params))
        const entries = Array.isArray(data.entries) ? data.entries : []
        this.pokedexEntries = reset || isFirst
          ? entries
          : this.pokedexEntries.concat(entries)
        this.pokedexPage = data.page ?? nextPage
        this.pokedexHasMore = Boolean(data.hasMore)
        this.pokedexTotal = Number(data.total) || 0
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load Pokédex.'
        this.pokedexHasMore = false
        if (isFirst) this.pokedexEntries = []
      } finally {
        this.pokedexLoading = false
        this.pokedexLoadingMore = false
        if (this.pickerStep === 'pokedex' && this.pokedexHasMore) {
          await this.afterPaint(() => this.fillPokedexIfNeeded())
        }
      }
    },

    async loadSets() {
      if (this.sets.length) return
      this.pickerLoading = true
      this.pickerError = ''
      try {
        const data = await getJson(this.urls.librarySets)
        this.sets = data.sets || []
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load sets.'
      } finally {
        this.pickerLoading = false
      }
    },

    async openSetCards(set) {
      this.selectedSet = set
      this.pickerStep = 'set-cards'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.pickerLoading = true
      this.pickerError = ''
      try {
        const data = await getJson(
          withParams(this.urls.libraryCards, {
            setName: set.rawSetName ?? set.name,
            includeWatched: 1,
          }),
        )
        this.pickerCards = data.cards || []
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load cards.'
        this.pickerCards = []
      } finally {
        this.pickerLoading = false
      }
    },

    async openPokemonCards(entry) {
      this.selectedPokemon = entry
      this.pickerStep = 'pokedex-cards'
      this.pickerFilter = ''
      this.selectedPickerCardIds = []
      this.pickerLoading = true
      this.pickerError = ''
      try {
        const data = await getJson(
          withParams(this.urls.libraryPokemonCards, {
            pokemonId: entry.id,
            includeWatched: 1,
          }),
        )
        this.pickerCards = data.cards || []
      } catch (err) {
        this.pickerError = err.message || 'Couldn’t load cards.'
        this.pickerCards = []
      } finally {
        this.pickerLoading = false
      }
    },

    async placeCard(card) {
      if (!this.activeSlotPageId || this.activeSlot?.slotIndex == null) {
        showSiteToast('Pick a pocket first.')
        return
      }
      this.busy = true
      try {
        const data = await postJson(
          this.urls.placeCard,
          {
            binderId: this.binder.id,
            pageId: this.activeSlotPageId,
            slotIndex: this.activeSlot.slotIndex,
            cardId: card.id,
          },
          config.csrfName,
          config.csrfToken,
        )
        this.applyBinder(data.binder)
        this.closePicker()
        showSiteToast(`Placed ${card.name}.`)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t place card.')
      } finally {
        this.busy = false
      }
    },

    async placeSelectedCards() {
      if (!this.activeSlotPageId || this.activeSlot?.slotIndex == null) {
        showSiteToast('Pick a pocket first.')
        return
      }
      if (!this.selectedPickerCardIds.length) {
        showSiteToast('Select at least one card.')
        return
      }
      if (!this.urls.placeCards) {
        showSiteToast('Multi-place isn’t available.')
        return
      }

      const count = this.selectedPickerCardIds.length
      this.busy = true
      try {
        const body = new FormData()
        body.append(config.csrfName, config.csrfToken)
        body.append('binderId', String(this.binder.id))
        body.append('pageId', String(this.activeSlotPageId))
        body.append('slotIndex', String(this.activeSlot.slotIndex))
        this.selectedPickerCardIds.forEach((id) => body.append('cardIds[]', String(id)))

        const res = await fetch(this.urls.placeCards, {
          method: 'POST',
          body,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || data.success === false) {
          throw new Error(data.error || `Request failed (${res.status})`)
        }

        this.applyBinder(data.binder)
        this.closePicker()
        const placed = Number(data.placed) || count
        showSiteToast(
          placed === 1 ? 'Placed 1 card.' : `Placed ${placed} cards.`,
        )
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t place cards.')
      } finally {
        this.busy = false
      }
    },
  }
}

export function createBinderViewer(config) {
  return {
    binder: config.binder,
    editUrl: config.editUrl || null,
    indexUrl: config.indexUrl || null,
    shareUrl: config.shareUrl || config.binder?.shareUrl || null,
    canEdit: Boolean(config.canEdit),
    isShared: Boolean(config.isShared),
    gallerySubmitter: config.gallerySubmitter || null,
    shareCopied: false,
    spreadIndex: 0,
    turning: false,

    async copyShareUrl() {
      const ok = await copyText(this.shareUrl)
      if (!ok) {
        showSiteToast('Couldn’t copy link — try again.')
        return
      }
      this.shareCopied = true
      showSiteToast('Share link copied.')
      window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task: 'sharedBinderUrl' } }))
      window.setTimeout(() => {
        this.shareCopied = false
      }, 2000)
    },

    slotImageUrl(slot) {
      return slot?.card?.imageUrl || slot?.customImage?.url || ''
    },

    get pageCount() {
      return this.binder.pages?.length || 0
    },

    // Mirrors the editor's spread pagination — page 1 sits alone on the
    // right (the left is the blank inside-cover), then pages pair up
    // (2,3) (4,5) …, so an even page count leaves the last page alone on
    // the left with the right pocket blank, just like a real binder.
    get spreadCount() {
      const total = this.pageCount
      if (total <= 1) return 1
      return 1 + Math.ceil((total - 1) / 2)
    },

    get canPrev() {
      return this.spreadIndex > 0
    },

    get canNext() {
      return this.spreadIndex < this.spreadCount - 1
    },

    get leftPage() {
      if (this.spreadIndex <= 0) return null
      return this.binder.pages[this.spreadIndex * 2 - 1] || null
    },

    get rightPage() {
      const idx = this.spreadIndex <= 0 ? 0 : this.spreadIndex * 2
      return this.binder.pages[idx] || null
    },

    get leftSlots() {
      return this.leftPage?.slots || this.emptySlots()
    },

    get rightSlots() {
      return this.rightPage?.slots || this.emptySlots()
    },

    visibleSlots(slots) {
      return (slots || []).filter((slot) => !slot?.covered)
    },

    get leftFolio() {
      return this.leftPage ? this.leftPage.label : ''
    },

    get rightFolio() {
      return this.rightPage ? this.rightPage.label : ''
    },

    get pageLabel() {
      const parts = [this.leftFolio, this.rightFolio].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Empty binder'
    },

    get sheetStyle() {
      return {
        '--binder-rows': String(this.binder.rows),
        '--binder-cols': String(this.binder.cols),
        '--binder-cover': this.binder.coverColor || '#1a1e27',
      }
    },

    get bookStyle() {
      return {
        '--binder-rows': String(this.binder.rows),
        '--binder-cols': String(this.binder.cols),
        '--binder-cover': this.binder.coverColor || '#1a1e27',
      }
    },

    emptySlots() {
      const n = (this.binder.rows || 1) * (this.binder.cols || 1)
      return Array.from({ length: n }, (_, i) => ({
        slotIndex: i,
        colSpan: 1,
        covered: false,
        card: null,
      }))
    },

    init() {
      this._onKey = (e) => {
        if (e.key === 'ArrowLeft') this.prevSpread()
        if (e.key === 'ArrowRight') this.nextSpread()
      }
      window.addEventListener('keydown', this._onKey)
    },

    destroy() {
      if (this._onKey) window.removeEventListener('keydown', this._onKey)
    },

    prevSpread() {
      if (!this.canPrev) return
      this.flashTurn()
      this.spreadIndex -= 1
    },

    nextSpread() {
      if (!this.canNext) return
      this.flashTurn()
      this.spreadIndex += 1
    },

    flashTurn() {
      this.turning = true
      window.setTimeout(() => {
        this.turning = false
      }, 420)
    },
  }
}

export function registerBinder(Alpine) {
  Alpine.data('binderIndex', () => {
    const config = readJson('binder-index-config')
    if (!config) {
      return {
        createOpen: false,
        openCreate() {},
        closeCreate() {},
      }
    }
    return createBinderIndex(config)
  })

  Alpine.data('binderEditor', () => {
    const config = readJson('binder-editor-config')
    if (!config?.binder) {
      return { binder: { pages: [], rows: 3, cols: 3, name: '' } }
    }
    return createBinderEditor(config)
  })

  Alpine.data('binderViewer', () => {
    const config = readJson('binder-viewer-config')
    if (!config?.binder) {
      return { binder: { pages: [], rows: 3, cols: 3, name: '' } }
    }
    return createBinderViewer(config)
  })
}
